import React, { FC, useRef, useState, useEffect } from 'react'
import { Platform, ScrollView, TouchableOpacity, View, Text, Image } from 'react-native'
import { useNavigate } from 'react-router-dom'
import DatePicker from 'react-datepicker'
import { getDatePickerLocale } from '@/utils/datepickerLocale'
import 'react-datepicker/dist/react-datepicker.css'
import {
  getHomeInfo,
  checkUserStatus,
  getPetDetailApi,
  getBrachycephalicBreeds,
  getDestinationBreedRestrictions,
  matchesAnyBreed,
  getF244Html,
  type IBrachycephalicBreed,
  type IDestinationBreedRestriction,
  type F244Data,
} from '@front/apis'
import { travelApi, TravelLogic, type ICreateTravelParams, type IAirline, type IAirport } from '@front/logic'
import { openHtmlInNewTab } from './docUtils'
import {
  AppBackground,
  BottomActionContainer,
  Button,
  Drop,
  Input,
  ResponsiveLogo,
  HeaderWithBack
} from '@ui/components'

import {
  InputContainer,
  InputWrapper,
  Headline2,
  Headline5,
  BodyText,
  ErrorText,
  colors,
  DatePickerWrapper,
  DisabledButton,
  ButtonContent,
  DisabledButtonText
} from '@/aena/styles' // Importando los estilos del PetAdd

import { ReactComponent as BackIcon } from '@/assets/icons/back_icon.svg'
import { ReactComponent as ChevronDownIcon } from '@/assets/icons/aena/aena_chevron_down.svg'

import { useAppSelector, useAppDispatch, loadSelectedPetGuid } from '@front/store'
import { HeaderWithBackAena } from '@/components/headerwithback'


// Tipos de viaje permitidos por mascota (el global se elimina)
const TRAVEL_TYPES = ['Cabina', 'Bodega'] as const
type TravelType = (typeof TRAVEL_TYPES)[number]

interface TravelCheckinForm {
  // Datos del viaje
  airline: string
  departureAirport: string
  arrivalAirport: string
  flightNumber: string
  locator: string
  flightDate: Date | null

  // Mascotas seleccionadas
  selectedPets: string[]                            // guids
  petTravelTypes: Record<string, TravelType>        // por mascota

  // Documentos de exoneración
  ownerName: string
  passengerDni: string
  phoneNumber: string
  contactEmail: string

  // Condiciones
  acceptsTravelConditions: boolean
  acceptsTransportConditions: boolean
  acceptsPrivacyPolicy: boolean

  // Firma
  signature: string | null
}

interface Pet {
  guid: string
  name: string
  avatar: string
  weight?: number
  stats?: { weightKg?: number }
  // TODO: el endpoint /home/petList no devuelve breed.
  // Lo cargamos bajo demanda via getPetDetailApi (ver enrichedPets state) y lo
  // mergeamos por guid; aquí permitimos que llegue por si en el futuro lo añadiesen.
  breed?: string
}

// ============================================================
// Mapa de fallback IATA -> ISO país (alpha-2).
// La tabla travel_airport del backend ya tiene country_iso, pero el endpoint
// público sólo devuelve `country` como nombre. Mientras esa API no incluya
// `countryIso`, mapeamos manualmente los aeropuertos más relevantes para
// rutas Air Europa (foco principal del webview AENA).
// ============================================================
const AIRPORT_ISO_FALLBACK: Record<string, string> = {
  // España
  MAD: 'ES', BCN: 'ES', PMI: 'ES', AGP: 'ES', VLC: 'ES', SVQ: 'ES',
  ALC: 'ES', BIO: 'ES', LPA: 'ES', TFN: 'ES', TFS: 'ES', IBZ: 'ES',
  ACE: 'ES', FUE: 'ES', SCQ: 'ES', OVD: 'ES', SDR: 'ES', VGO: 'ES',
  // Europa
  CDG: 'FR', ORY: 'FR', NCE: 'FR', LYS: 'FR', TLS: 'FR', MRS: 'FR',
  LHR: 'GB', LGW: 'GB', STN: 'GB', LTN: 'GB', MAN: 'GB', EDI: 'GB',
  FRA: 'DE', MUC: 'DE', TXL: 'DE', BER: 'DE', DUS: 'DE', HAM: 'DE',
  AMS: 'NL', BRU: 'BE', LUX: 'LU', ZRH: 'CH', GVA: 'CH',
  FCO: 'IT', MXP: 'IT', LIN: 'IT', VCE: 'IT', NAP: 'IT',
  LIS: 'PT', OPO: 'PT', FNC: 'PT',
  VIE: 'AT', DUB: 'IE', CPH: 'DK', OSL: 'NO', ARN: 'SE', HEL: 'FI',
  WAW: 'PL', PRG: 'CZ', BUD: 'HU', ATH: 'GR',
  // América
  JFK: 'US', EWR: 'US', MIA: 'US', LAX: 'US', ORD: 'US', BOS: 'US',
  IAD: 'US', SFO: 'US', SJU: 'PR',
  YYZ: 'CA', YUL: 'CA', YVR: 'CA',
  GRU: 'BR', GIG: 'BR', SSA: 'BR', FOR: 'BR', REC: 'BR',
  MEX: 'MX', CUN: 'MX', GDL: 'MX',
  EZE: 'AR', AEP: 'AR', BOG: 'CO', UIO: 'EC', LIM: 'PE', SCL: 'CL',
  CCS: 'VE', PTY: 'PA', HAV: 'CU', SDQ: 'DO', PUJ: 'DO', MVD: 'UY',
  ASU: 'PY', LPB: 'BO', SJO: 'CR',
  // Asia / África / Oceanía
  DXB: 'AE', AUH: 'AE', DOH: 'QA', IST: 'TR',
  CMN: 'MA', RAK: 'MA', RBA: 'MA',
  TLV: 'IL', CAI: 'EG',
  HKG: 'HK', PEK: 'CN', PVG: 'CN', NRT: 'JP', HND: 'JP', ICN: 'KR',
  BKK: 'TH', SIN: 'SG', KUL: 'MY', DEL: 'IN', BOM: 'IN',
  SYD: 'AU', MEL: 'AU', AKL: 'NZ',
}

// Fallback secundario por nombre/sufijo de país que viene del backend (case-insensitive).
const COUNTRY_NAME_TO_ISO: Record<string, string> = {
  españa: 'ES', spain: 'ES',
  francia: 'FR', france: 'FR',
  alemania: 'DE', germany: 'DE',
  'reino unido': 'GB', 'united kingdom': 'GB',
  italia: 'IT', italy: 'IT',
  portugal: 'PT',
  países: 'NL', 'países bajos': 'NL', netherlands: 'NL', holanda: 'NL',
  bélgica: 'BE', belgium: 'BE',
  suiza: 'CH', switzerland: 'CH',
  austria: 'AT', irlanda: 'IE', ireland: 'IE',
  dinamarca: 'DK', denmark: 'DK',
  suecia: 'SE', sweden: 'SE',
  noruega: 'NO', norway: 'NO',
  finlandia: 'FI', finland: 'FI',
  grecia: 'GR', greece: 'GR',
  polonia: 'PL', poland: 'PL',
  'república checa': 'CZ', 'czech republic': 'CZ', czechia: 'CZ',
  hungría: 'HU', hungary: 'HU',
  'estados unidos': 'US', 'united states': 'US', usa: 'US',
  canadá: 'CA', canada: 'CA',
  méxico: 'MX', mexico: 'MX',
  brasil: 'BR', brazil: 'BR',
  argentina: 'AR', colombia: 'CO', ecuador: 'EC', perú: 'PE', peru: 'PE',
  chile: 'CL', venezuela: 'VE', panamá: 'PA', panama: 'PA',
  cuba: 'CU', 'república dominicana': 'DO', 'dominican republic': 'DO',
  uruguay: 'UY', paraguay: 'PY', bolivia: 'BO', 'costa rica': 'CR',
  'puerto rico': 'PR',
  marruecos: 'MA', morocco: 'MA',
  'emiratos árabes': 'AE', 'united arab emirates': 'AE', uae: 'AE',
  catar: 'QA', qatar: 'QA',
  turquía: 'TR', turkey: 'TR',
  israel: 'IL', egipto: 'EG', egypt: 'EG',
  china: 'CN', japón: 'JP', japan: 'JP',
  'corea del sur': 'KR', 'south korea': 'KR',
  tailandia: 'TH', thailand: 'TH', singapur: 'SG', singapore: 'SG',
  malasia: 'MY', malaysia: 'MY', india: 'IN',
  australia: 'AU', 'nueva zelanda': 'NZ', 'new zealand': 'NZ',
  hong: 'HK',
}

function getIsoForAirport(airport: IAirport | undefined): string {
  if (!airport) return ''
  const byCode = AIRPORT_ISO_FALLBACK[airport.code?.toUpperCase()?.trim() ?? '']
  if (byCode) return byCode
  const name = (airport.country ?? '').toLowerCase().trim()
  if (!name) return ''
  if (COUNTRY_NAME_TO_ISO[name]) return COUNTRY_NAME_TO_ISO[name]
  // intento por palabra inicial (ej. "Países Bajos" -> "países" no es único pero ayuda).
  const firstWord = name.split(/\s+/)[0]
  if (firstWord && COUNTRY_NAME_TO_ISO[firstWord]) return COUNTRY_NAME_TO_ISO[firstWord]
  // si en algún momento el endpoint devuelve directamente ISO de 2 letras
  if (/^[a-z]{2}$/.test(name)) return name.toUpperCase()
  return ''
}

export const AenaTravelAdd: FC = () => {
  const navigate = useNavigate()
  const signatureCanvasRef = useRef<HTMLCanvasElement | null>(null)
  const [homeData, setHomeData] = useState<any>({})
  const [userData, setUserData] = useState<any>({})
  const { guid: petGUID } = useAppSelector((state) => state.selectedPet)
  const dispatch = useAppDispatch()
  const [selectedPet, setSelectedPet] = useState<string>(petGUID)
  const scrollViewRef = useRef<ScrollView>(null)
  const petRefs = useRef<Record<string, View | null>>({})

  // Estados para datos de la BD
  const [airlines, setAirlines] = useState<IAirline[]>([])
  const [airports, setAirports] = useState<IAirport[]>([])
  const [loadingData, setLoadingData] = useState(true)

  // ===== Restricciones de raza
  const [brachyBreeds, setBrachyBreeds] = useState<IBrachycephalicBreed[]>([])
  const [destinationRestrictions, setDestinationRestrictions] = useState<IDestinationBreedRestriction[]>([])
  // breed lookup por mascota (fetched on demand desde getPetDetailApi)
  const [petBreeds, setPetBreeds] = useState<Record<string, string>>({})

  useEffect(() => {
    const fetchHomeInfo = async () => {
      const home = await getHomeInfo({ lat: 0, lon: 0 })
      setHomeData(home.data)
      const petToSelect = petGUID !== '' ? petGUID : home.data?.petList?.[0]?.guid ?? ''
      setSelectedPet(petToSelect)
    }

    const resUserStatus = async () => {
      const userstatus = await checkUserStatus({ lat: 0, lon: 0 })
      setUserData(userstatus.data)
    }

    const fetchTravelData = async () => {
      try {
        setLoadingData(true)
        const [airlinesResponse, airportsResponse] = await Promise.all([
          travelApi.getAirlines(),
          travelApi.getAirports()
        ])

        setAirlines(
          airlinesResponse.data.filter(
            (a) => a.name?.toLowerCase().includes('air europa') || a.code === 'UX'
          )
        )
        setAirports(airportsResponse.data)
      } catch (error) {
        console.error('Error loading travel data:', error)
      } finally {
        setLoadingData(false)
      }
    }

    const fetchBrachy = async () => {
      try {
        const list = await getBrachycephalicBreeds()
        setBrachyBreeds(list)
      } catch (err) {
        console.warn('No se pudieron cargar razas braquicefálicas', err)
      }
    }

    fetchHomeInfo()
    resUserStatus()
    fetchTravelData()
    fetchBrachy()

    // debug idioma si hace falta
    // console.log('currentLang', i18n.language)
  }, [])

  const [formData, setFormData] = useState<TravelCheckinForm>({
    airline: '',
    departureAirport: '',
    arrivalAirport: '',
    flightNumber: '',
    locator: '',
    flightDate: null,
    selectedPets: [],
    petTravelTypes: {},
    ownerName: '',
    passengerDni: '',
    phoneNumber: '',
    contactEmail: '',
    acceptsTravelConditions: false,
    acceptsTransportConditions: false,
    acceptsPrivacyPolicy: false,
    signature: null
  })

  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({})
  const [isDrawing, setIsDrawing] = useState(false)
  const [showSignatureConfirmation, setShowSignatureConfirmation] = useState(false)
  // Gate de vacunas: bloquea el botón mientras se comprueba la elegibilidad async.
  const [checkingVaccines, setCheckingVaccines] = useState(false)

  // ===== Enriquecimiento de razas: si homeData.petList no incluye breed,
  // pedimos el detalle de cada mascota y mergeamos por guid.
  useEffect(() => {
    const pets: Pet[] | undefined = homeData?.petList
    if (!pets || pets.length === 0) return

    const pending = pets.filter((p) => !petBreeds[p.guid] && !p.breed)
    if (pending.length === 0) return

    let cancelled = false
    ;(async () => {
      const results: Record<string, string> = {}
      await Promise.all(
        pending.map(async (p) => {
          try {
            const res = await getPetDetailApi({ guid: p.guid })
            const breed = res?.data?.petBreedName || ''
            if (breed) results[p.guid] = breed
          } catch (err) {
            console.debug('No se pudo obtener detalle de mascota', p.guid, err)
          }
        }),
      )
      if (!cancelled && Object.keys(results).length > 0) {
        setPetBreeds((prev) => ({ ...prev, ...results }))
      }
    })()
    return () => {
      cancelled = true
    }
  }, [homeData])

  // ===== Restricciones por país de destino
  useEffect(() => {
    const code = formData?.arrivalAirport
    if (!code) {
      setDestinationRestrictions([])
      return
    }
    const airport = airports.find((a) => a.code === code)
    const iso = getIsoForAirport(airport)
    if (!iso) {
      setDestinationRestrictions([])
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const list = await getDestinationBreedRestrictions(iso)
        if (!cancelled) setDestinationRestrictions(list)
      } catch (err) {
        console.warn('No se pudieron cargar restricciones de raza por destino', err)
        if (!cancelled) setDestinationRestrictions([])
      }
    })()
    return () => {
      cancelled = true
    }
  }, [formData?.arrivalAirport, airports])

  // ===== Helpers de mascotas / peso / tipos permitidos
  const getPetByGuid = (guid: string): Pet | undefined =>
    homeData?.petList?.find((p: Pet) => p.guid === guid)

  const getPetWeightKg = (pet?: Pet): number => {
    const raw = pet?.weight ?? pet?.stats?.weightKg ?? 0
    const n = typeof raw === 'string' ? parseFloat(raw) : raw
    return Number.isFinite(n as number) ? (n as number) : 0
  }

  const getPetBreed = (pet?: Pet): string => {
    if (!pet) return ''
    return pet.breed || petBreeds[pet.guid] || ''
  }

  const isBrachyPet = (pet?: Pet): boolean => {
    const breed = getPetBreed(pet)
    if (!breed) return false
    return matchesAnyBreed(breed, brachyBreeds)
  }

  // Tipos de viaje permitidos combinando peso + raza braquicefálica.
  // - Braquicéfalo:
  //     peso <= 10 kg => solo Cabina (riesgo respiratorio en bodega presurizada)
  //     peso > 10 kg  => no puede viajar (lista vacía)
  // - No braquicéfalo (regla previa):
  //     peso <= 8 kg  => Cabina o Bodega
  //     peso > 8 kg   => solo Bodega
  const allowedTypesForPet = (pet?: Pet): TravelType[] => {
    const w = getPetWeightKg(pet)
    if (isBrachyPet(pet)) {
      if (w > 10) return [] // bloqueado totalmente
      return ['Cabina']
    }
    return (w > 8 ? ['Bodega'] : ['Cabina', 'Bodega']) as TravelType[]
  }

  // Restricción de país aplicable a la mascota seleccionada.
  // Devuelve la entrada con mayor prioridad (block > warn) si hay match.
  const getDestinationRestrictionForPet = (
    pet?: Pet,
  ): IDestinationBreedRestriction | undefined => {
    if (!pet || destinationRestrictions.length === 0) return undefined
    const breed = getPetBreed(pet)
    if (!breed) return undefined
    const blocks = destinationRestrictions.filter((r) => r.restriction === 'block')
    if (matchesAnyBreed(breed, blocks)) {
      return blocks.find((r) => matchesAnyBreed(breed, [r]))
    }
    const warns = destinationRestrictions.filter((r) => r.restriction === 'warn')
    if (matchesAnyBreed(breed, warns)) {
      return warns.find((r) => matchesAnyBreed(breed, [r]))
    }
    return undefined
  }

  // ===== Datos agregados para el banner de destino
  const destinationIso = getIsoForAirport(
    airports.find((a) => a.code === formData?.arrivalAirport),
  )
  const destinationCountryName =
    airports.find((a) => a.code === formData?.arrivalAirport)?.country || destinationIso
  const blockRestrictions = destinationRestrictions.filter((r) => r.restriction === 'block')
  const warnRestrictions = destinationRestrictions.filter((r) => r.restriction === 'warn')

  // ===== Selectores y entradas
  const mapToSelectable = (arr: string[]) =>
    arr.map((v) => ({ title: v, id: v, data: v }))

  const mapAirlinesToSelectable = (airlines: IAirline[]) =>
    airlines.map((airline) => ({ title: airline.name, id: airline.code, data: airline.code }))

  const mapAirportsToSelectable = (airports: IAirport[]) =>
    airports.map((airport) => ({ title: `${airport.city} - ${airport.name} (${airport.code})`, id: airport.code, data: airport.code }))

  const findIndex = (arr: string[], val: string) =>
    arr.findIndex((v) => v === val)

  const findAirlineIndex = (airlines: IAirline[], val: string) =>
    airlines.findIndex((airline) => airline.code === val)

  const findAirportIndex = (airports: IAirport[], val: string) =>
    airports.findIndex((airport) => airport.code === val)

  // Validadores de formato reutilizados en vivo (mientras se escribe) y al enviar.
  // requireValue=false NO muestra "obligatorio" mientras el campo está vacío (para
  // no molestar al empezar a escribir); requireValue=true sí (al enviar).
  const phoneError = (value: string, requireValue: boolean): string => {
    const raw = (value || '').trim()
    if (!raw) return requireValue ? 'El teléfono es obligatorio' : ''
    const digits = raw.replace(/[\s-]/g, '').replace(/^\+/, '')
    if (!/^\+?[0-9\s-]{9,20}$/.test(raw) || digits.length < 9 || digits.length > 15) {
      return 'Teléfono no válido (mínimo 9 dígitos)'
    }
    return ''
  }
  const emailError = (value: string, requireValue: boolean): string => {
    const raw = (value || '').trim()
    if (!raw) return requireValue ? 'El email es obligatorio' : ''
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw)) return 'Email no válido'
    return ''
  }
  // Validador de DNI (8 dígitos + letra de control) y NIE (X/Y/Z + 7 dígitos + letra).
  // Letra de control = 'TRWAGMYFPDXBNJZSQVHLCKE'[numero % 23].
  // Para NIE se antepone X=0, Y=1, Z=2 al número antes de calcular el módulo.
  const dniError = (value: string, requireValue: boolean): string => {
    const raw = (value || '').trim().toUpperCase()
    if (!raw) return requireValue ? 'El DNI/NIE es obligatorio' : ''
    const CONTROL = 'TRWAGMYFPDXBNJZSQVHLCKE'
    const dniMatch = /^(\d{8})([A-Z])$/.exec(raw)
    const nieMatch = /^([XYZ])(\d{7})([A-Z])$/.exec(raw)
    if (dniMatch) {
      const num = parseInt(dniMatch[1], 10)
      const expected = CONTROL[num % 23]
      if (expected !== dniMatch[2]) return 'DNI no válido (letra de control incorrecta)'
      return ''
    }
    if (nieMatch) {
      const prefix = { X: '0', Y: '1', Z: '2' }[nieMatch[1] as 'X' | 'Y' | 'Z']
      const num = parseInt(`${prefix}${nieMatch[2]}`, 10)
      const expected = CONTROL[num % 23]
      if (expected !== nieMatch[3]) return 'NIE no válido (letra de control incorrecta)'
      return ''
    }
    return 'DNI/NIE no válido (8 dígitos + letra, o X/Y/Z + 7 dígitos + letra)'
  }

  const handleInputChange = (field: keyof TravelCheckinForm, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Validación EN VIVO de formato para teléfono, email y DNI mientras se escribe.
    if (field === 'phoneNumber') {
      setFieldErrors(prev => ({ ...prev, phoneNumber: phoneError(value, false) }))
    } else if (field === 'contactEmail') {
      setFieldErrors(prev => ({ ...prev, contactEmail: emailError(value, false) }))
    } else if (field === 'passengerDni') {
      setFieldErrors(prev => ({ ...prev, passengerDni: dniError(value, false) }))
    }
  }

  // ===== Preview del descargo de responsabilidad F244
  // Construye los datos del F244 a partir del formulario actual + la firma
  // dibujada y abre el PREVIEW "bonito" en una pestaña nueva. Disponible siempre
  // (no depende de aceptar las condiciones) para que el usuario pueda revisarlo.
  const openF244Preview = () => {
    const firstPetGuid = formData.selectedPets[0]
    const firstPet = firstPetGuid ? getPetByGuid(firstPetGuid) : undefined
    const data: F244Data = {
      passengerName: formData.ownerName || '',
      flightNumber: formData.flightNumber || '',
      flightDateISO: formData.flightDate ? formData.flightDate.toISOString() : '',
      route: TravelLogic.generateRouteText(
        formData.departureAirport || '',
        formData.arrivalAirport || ''
      ),
      phone: formData.phoneNumber || '',
      signatureDataUrl: formData.signature || null,
      passengerDni: formData.passengerDni || null,
      email: formData.contactEmail || null,
      locator: formData.locator || null,
      petName: firstPet?.name || null,
      petBreed: (firstPet as any)?.breed || null,
      petWeightKg: firstPet ? getPetWeightKg(firstPet) : null,
      petTravelType: firstPetGuid ? (formData.petTravelTypes[firstPetGuid] || null) : null,
    }
    // Usamos el MISMO render oficial (2 columnas ES | EN) que el documento final
    // emitido, para que el preview al crear sea idéntico a lo que se ve después.
    openHtmlInNewTab(getF244Html(data))
  }

  // ===== Selección múltiple de mascotas
  const togglePetSelection = (guid: string) => {
    setFormData(prev => {
      const already = prev.selectedPets.includes(guid)
      if (already) {
        const { [guid]: _, ...rest } = prev.petTravelTypes
        return {
          ...prev,
          selectedPets: prev.selectedPets.filter(id => id !== guid),
          petTravelTypes: rest
        }
      } else {
        const pet = getPetByGuid(guid)
        const options = allowedTypesForPet(pet)
        // Si no hay opciones (braqui > 10 kg) seleccionamos igual la mascota
        // para que el usuario vea por qué está bloqueada; la validación
        // posterior impedirá el envío.
        const next = { ...prev.petTravelTypes }
        if (options.length > 0) {
          next[guid] = options[0]
        }
        return {
          ...prev,
          selectedPets: [...prev.selectedPets, guid],
          petTravelTypes: next,
        }
      }
    })
  }

  // ===== Firma (canvas)
  const getCanvasCoordinates = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = signatureCanvasRef.current
    if (!canvas) return { x: 0, y: 0 }

    const rect = canvas.getBoundingClientRect()
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height

    let clientX: number, clientY: number
    if ('touches' in e) {
      const touch = e.touches[0] || e.changedTouches[0]
      clientX = touch.clientX
      clientY = touch.clientY
    } else {
      const me = e as React.MouseEvent<HTMLCanvasElement>
      clientX = me.clientX
      clientY = me.clientY
    }

    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    }
  }

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault()
    setIsDrawing(true)
    const canvas = signatureCanvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const coords = getCanvasCoordinates(e)
    ctx.lineWidth = 2
    ctx.lineCap = 'round'
    ctx.strokeStyle = colors.negro
    ctx.beginPath()
    ctx.moveTo(coords.x, coords.y)
  }

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault()
    if (!isDrawing) return
    const canvas = signatureCanvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const coords = getCanvasCoordinates(e)
    ctx.lineTo(coords.x, coords.y)
    ctx.stroke()
  }

  const stopDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault()
    setIsDrawing(false)
    const canvas = signatureCanvasRef.current
    if (!canvas) return
    const dataURL = canvas.toDataURL()
    setFormData(prev => ({ ...prev, signature: dataURL }))
  }

  const clearSignature = () => {
    const canvas = signatureCanvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    setFormData(prev => ({ ...prev, signature: null }))
  }

  // ===== Validación
  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {}

    // Datos del viaje
    if (!formData.airline) errors.airline = 'La aerolínea es obligatoria'
    if (!formData.departureAirport) errors.departureAirport = 'El aeropuerto de salida es obligatorio'
    if (!formData.arrivalAirport) errors.arrivalAirport = 'El aeropuerto de destino es obligatorio'
    if (!formData.flightNumber?.trim()) {
      errors.flightNumber = 'El número de vuelo es obligatorio'
    } else if (!/^[A-Z]{2,3}\d{1,4}$/i.test(formData.flightNumber.trim())) {
      errors.flightNumber = 'Formato no válido. Debe ser 2-3 letras + 1-4 dígitos (ej. UX7791)'
    }
    if (!formData.locator?.trim()) {
      errors.locator = 'El localizador es obligatorio'
    } else if (!/^[A-Z0-9]{6}$/i.test(formData.locator.trim())) {
      errors.locator = 'El localizador debe tener exactamente 6 caracteres alfanuméricos'
    }
    if (!formData.flightDate) errors.flightDate = 'La fecha de vuelo es obligatoria'

    // Mascotas
    if (formData.selectedPets.length === 0) {
      errors.selectedPets = 'Debes seleccionar al menos una mascota'
    } else {
      for (const guid of formData.selectedPets) {
        const pet = getPetByGuid(guid)
        const val = formData.petTravelTypes[guid]
        const allowed = allowedTypesForPet(pet)
        const weight = getPetWeightKg(pet)

        // Bloqueo total: braquicéfalo > 10 kg no puede viajar
        if (isBrachyPet(pet) && weight > 10) {
          errors.selectedPets = `${pet?.name ?? 'La mascota'} es de raza braquicefálica y supera 10 kg: no puede viajar por riesgo respiratorio.`
          break
        }

        // Bloqueo por país de destino (block)
        const destRestriction = getDestinationRestrictionForPet(pet)
        if (destRestriction?.restriction === 'block') {
          errors.selectedPets = `${pet?.name ?? 'La mascota'} (raza ${destRestriction.breedName}) tiene la entrada prohibida en ${destinationCountryName || destinationIso}.`
          break
        }

        if (allowed.length === 0) {
          errors.selectedPets = `${pet?.name ?? 'La mascota'} no puede viajar con las restricciones actuales.`
          break
        }
        if (!val) {
          errors.selectedPets = 'Debes seleccionar el tipo de viaje para cada mascota'
          break
        }
        if (!allowed.includes(val)) {
          errors.selectedPets = `El tipo de viaje seleccionado no es válido para ${pet?.name}`
          break
        }
      }
    }

    // Documentos
    if (!formData.ownerName?.trim()) errors.ownerName = 'El nombre es obligatorio'

    // DNI/NIE: obligatorio + formato (misma lógica que la validación en vivo).
    const dniErr = dniError(formData.passengerDni, true)
    if (dniErr) errors.passengerDni = dniErr

    // Teléfono y email: obligatorio + formato (misma lógica que la validación en vivo).
    const phoneErr = phoneError(formData.phoneNumber, true)
    if (phoneErr) errors.phoneNumber = phoneErr
    const emailErr = emailError(formData.contactEmail, true)
    if (emailErr) errors.contactEmail = emailErr

    // Condiciones
    if (!formData.acceptsTravelConditions || !formData.acceptsTransportConditions) {
      errors.acceptsTravelConditions = 'Debes aceptar las condiciones de viaje con mascota y el estado y forma del transportín'
    }
    if (!formData.acceptsPrivacyPolicy) errors.acceptsPrivacyPolicy = 'Debes aceptar las políticas de privacidad'

    // Firma
    if (!formData.signature) errors.signature = 'La firma es obligatoria'

    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) return

    // ===== GATE de vacunas (async) ANTES de crear el viaje.
    // validateForm es sync y la elegibilidad de vacunas es async, así que la
    // comprobamos aquí. Para cada mascota seleccionada consultamos
    // checkPetEligibility; si alguna no es elegible mostramos sus reasons y
    // abortamos sin crear el viaje.
    const flightDateISO = formData.flightDate?.toISOString() || ''
    setCheckingVaccines(true)
    try {
      const blockingMessages: string[] = []
      for (const guid of formData.selectedPets) {
        const pet = getPetByGuid(guid)
        try {
          const res = await travelApi.checkPetEligibility({
            petGUID: guid,
            departureAirport: formData.departureAirport,
            arrivalAirport: formData.arrivalAirport,
            flightDate: flightDateISO,
          })
          const eligible = res?.data?.eligible
          const reasons = res?.data?.reasons ?? []
          if (!eligible) {
            const petName = pet?.name ?? 'La mascota'
            const detail = reasons.length > 0 ? reasons.join('; ') : 'no cumple los requisitos de vacunación'
            blockingMessages.push(`${petName}: ${detail}`)
          }
        } catch (err) {
          console.error('Error comprobando elegibilidad de vacunas', guid, err)
          const petName = pet?.name ?? 'La mascota'
          blockingMessages.push(`${petName}: no se pudo comprobar la vacunación. Inténtalo de nuevo.`)
        }
      }
      if (blockingMessages.length > 0) {
        setFieldErrors(prev => ({ ...prev, selectedPets: blockingMessages.join('\n') }))
        return
      }
    } finally {
      setCheckingVaccines(false)
    }

    // ===== GATE de documentación de EXTERIOR para vuelos internacionales.
    // Si el viaje sale/llega fuera de España, cada mascota necesita la
    // "Documentación para viajes al extranjero" (pasaporte UE). Si falta, no se
    // deja continuar y se indica al usuario que la complete.
    const depIso = getIsoForAirport(airports.find((a) => a.code === formData.departureAirport))
    const arrIso = getIsoForAirport(airports.find((a) => a.code === formData.arrivalAirport))
    const isInternational = (!!depIso && depIso !== 'ES') || (!!arrIso && arrIso !== 'ES')
    if (isInternational) {
      const missingDocs = formData.selectedPets
        .map((g) => getPetByGuid(g))
        .filter((p) => p && (p as any).hasExteriorDoc === false)
        .map((p) => p?.name ?? 'La mascota')
      if (missingDocs.length > 0) {
        setFieldErrors(prev => ({
          ...prev,
          selectedPets: `Para vuelos internacionales necesitas la "Documentación para viajes al extranjero" (pasaporte) de: ${missingDocs.join(', ')}. Complétala en el pasaporte digital.`,
        }))
        return
      }
    }

    setShowSignatureConfirmation(true)

    try {
      // Preparar datos para envío
      const travelData: ICreateTravelParams = {
        airline: formData.airline,
        departureAirport: formData.departureAirport,
        arrivalAirport: formData.arrivalAirport,
        flightNumber: formData.flightNumber,
        locator: formData.locator,
        flightDate: formData.flightDate?.toISOString() || '',
        selectedPets: formData.selectedPets,
        petTravelTypes: formData.petTravelTypes,
        ownerName: formData.ownerName,
        passengerDni: formData.passengerDni,
        phoneNumber: formData.phoneNumber,
        contactEmail: formData.contactEmail,
        acceptsTravelConditions: formData.acceptsTravelConditions,
        acceptsTransportConditions: formData.acceptsTransportConditions,
        acceptsPrivacyPolicy: formData.acceptsPrivacyPolicy,
        signature: formData.signature || '',
      }
      console.debug({ travelData })
      await travelApi.createTravel(travelData)
      // Seleccionar la mascota del viaje recién creado y llevar a "Mis viajes",
      // así el usuario ve de inmediato el viaje que acaba de crear (la lista filtra
      // por mascota seleccionada).
      const createdPetGuid = formData.selectedPets[0]
      if (createdPetGuid) dispatch(loadSelectedPetGuid(createdPetGuid))
      navigate('/aena/travel')
    } catch (error: any) {
      console.error('Error al crear el viaje:', error)
      // Surface el mensaje real del backend (p.ej. gate de vacunas o de documentación
      // internacional) en vez de un alert genérico.
      const msg = error?.message || error?.data?.message || 'Error al crear el viaje. Inténtalo de nuevo.'
      setShowSignatureConfirmation(false)
      setFieldErrors(prev => ({ ...prev, selectedPets: msg }))
    } finally {
      setShowSignatureConfirmation(false)
    }
  }

  const onBackPress = () => {
    navigate(-1)
  }

  // Todas las mascotas seleccionadas tienen tipo
  const allPetsHaveType =
    formData.selectedPets.length > 0 &&
    formData.selectedPets.every(g => !!formData.petTravelTypes[g])

  // Ninguna mascota tiene un bloqueo "duro" (braqui >10 kg o block por país)
  const noHardBlockedPet = formData.selectedPets.every((g) => {
    const pet = getPetByGuid(g)
    if (!pet) return true
    if (isBrachyPet(pet) && getPetWeightKg(pet) > 10) return false
    const destR = getDestinationRestrictionForPet(pet)
    if (destR?.restriction === 'block') return false
    return true
  })

  // Checkbox unificado: ambos booleanos se ponen al mismo valor a la vez,
  // así que basta comprobar que las condiciones (viaje + transportín) están aceptadas.
  const acceptsTravelAndTransportConditions =
    formData.acceptsTravelConditions && formData.acceptsTransportConditions

  const isFormValid =
    acceptsTravelAndTransportConditions &&
    formData.acceptsPrivacyPolicy &&
    !!formData.signature &&
    allPetsHaveType &&
    noHardBlockedPet

  // ===== Pantalla intermedia de "Procesando firmas"
  if (showSignatureConfirmation) {
    return (
      <AppBackground isSafe>
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 }}>
          <View style={{ backgroundColor: 'white', borderRadius: 16, padding: 24, alignItems: 'center', minWidth: 300 }}>
            <Headline2 style={{ marginBottom: 20, textAlign: 'center' }}>
              Procesando firmas
            </Headline2>

            {formData.selectedPets.map(guid => {
              const pet = getPetByGuid(guid)
              return (
                <View
                  key={guid}
                  style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 16, width: '100%' }}
                >
                  <BodyText style={{ flex: 1 }}>{pet?.name}</BodyText>
                  <View style={{ marginLeft: 16 }}>
                    {/* Simular spinner */}
                    <Text style={{ fontSize: 20, color: colors.aenaVerde }}>⟳</Text>
                  </View>
                </View>
              )
            })}

            <BodyText style={{ textAlign: 'center', marginTop: 16, color: colors.grisOscuro }}>
              Por favor, espera mientras procesamos la información...
            </BodyText>
          </View>
        </View>
      </AppBackground>
    )
  }

  return (
    <AppBackground isSafe>
      <View style={{ maxWidth: 800, width: '100%', alignSelf: 'center' }}>
        <View style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', padding: 16, gap: 16 }}>
          <ResponsiveLogo
            breakpoint={820}
            hysteresis={20}
            large={{ width: 680, height: 96 }}
            small={{ width: 360, height: 48 }}
          />
        </View>

        <HeaderWithBackAena title="Nuevo viaje" navigateTo="/aena/travel" />

        <BottomActionContainer
          bottomAction={
            checkingVaccines ? (
              <DisabledButton disabled>
                <ButtonContent>
                  <DisabledButtonText>Comprobando vacunas…</DisabledButtonText>
                </ButtonContent>
              </DisabledButton>
            ) : isFormValid ? (
              <Button
                title="Firmar y enviar"
                size="block"
                textType="cta_medium"
                borderRadius={5}
                buttonWrapperStyle={{ backgroundColor: colors.aenaVerde }}
                color="primary"
                onPress={handleSubmit}
              />
            ) : (
              <DisabledButton disabled>
                <ButtonContent>
                  <DisabledButtonText>Firmar y enviar</DisabledButtonText>
                </ButtonContent>
              </DisabledButton>
            )
          }
        >
          <ScrollView>

            {/* ================= SECCIÓN 1: DATOS DEL VIAJE ================= */}
            <View style={{ marginBottom: 24 }}>
              {/* Aerolínea */}
              <InputWrapper>
                <Headline5>Aerolínea *</Headline5>
                <InputContainer>
                  <Drop
                    data={mapAirlinesToSelectable(airlines)}
                    active={findAirlineIndex(airlines, formData.airline)}
                    selectedItem={findAirlineIndex(airlines, formData.airline)}
                    onPress={(item) => handleInputChange('airline', item.data)}
                    placeholder={loadingData ? "Cargando aerolíneas..." : "Selecciona una aerolínea"}
                    icon={<ChevronDownIcon width={22} height={22} />}
                    activeColor={colors.aenaVerde}
                    color="aena"
                  />
                  {fieldErrors.airline && <ErrorText>{fieldErrors.airline}</ErrorText>}
                </InputContainer>
              </InputWrapper>

              {/* Aeropuertos */}
              <View style={{ flexDirection: 'row', gap: 16 }}>
                <View style={{ flex: 1 }}>
                  <Headline5>Aeropuerto de Salida *</Headline5>
                  <InputContainer>
                    <Drop
                      data={mapAirportsToSelectable(airports)}
                      active={findAirportIndex(airports, formData.departureAirport)}
                      selectedItem={findAirportIndex(airports, formData.departureAirport)}
                      onPress={(item) => handleInputChange('departureAirport', item.data)}
                      placeholder={loadingData ? "Cargando aeropuertos..." : "Aeropuerto origen"}
                      icon={<ChevronDownIcon width={22} height={22} />}
                      activeColor={colors.aenaVerde}
                      color="aena"
                    />
                    {fieldErrors.departureAirport && <ErrorText>{fieldErrors.departureAirport}</ErrorText>}
                  </InputContainer>
                </View>

                <View style={{ flex: 1 }}>
                  <Headline5>Aeropuerto de Destino *</Headline5>
                  <InputContainer>
                    <Drop
                      data={mapAirportsToSelectable(airports)}
                      active={findAirportIndex(airports, formData.arrivalAirport)}
                      selectedItem={findAirportIndex(airports, formData.arrivalAirport)}
                      onPress={(item) => handleInputChange('arrivalAirport', item.data)}
                      placeholder={loadingData ? "Cargando aeropuertos..." : "Aeropuerto destino"}
                      icon={<ChevronDownIcon width={22} height={22} />}
                      activeColor={colors.aenaVerde}
                      color="aena"
                    />
                    {fieldErrors.arrivalAirport && <ErrorText>{fieldErrors.arrivalAirport}</ErrorText>}
                  </InputContainer>
                </View>
              </View>

              {/* Banner restricciones por país de destino */}
              {formData.arrivalAirport && (blockRestrictions.length > 0 || warnRestrictions.length > 0) && (
                <View style={{ marginTop: 4, marginBottom: 8 }}>
                  {blockRestrictions.length > 0 && (
                    <View
                      style={{
                        backgroundColor: '#fdecec',
                        borderColor: colors.rojoInactivo,
                        borderWidth: 1,
                        borderRadius: 8,
                        padding: 12,
                        marginBottom: warnRestrictions.length > 0 ? 8 : 0,
                      }}
                    >
                      <BodyText style={{ fontWeight: 'bold', color: colors.rojoInactivo, marginBottom: 4 }}>
                        Restricciones en {destinationCountryName}
                      </BodyText>
                      <BodyText style={{ color: colors.rojoInactivo }}>
                        Las siguientes razas tienen prohibida la entrada:{' '}
                        {blockRestrictions.map((r) => r.breedName).join(', ')}.
                      </BodyText>
                    </View>
                  )}
                  {warnRestrictions.length > 0 && (
                    <View
                      style={{
                        backgroundColor: '#fff8e0',
                        borderColor: '#d49a00',
                        borderWidth: 1,
                        borderRadius: 8,
                        padding: 12,
                      }}
                    >
                      <BodyText style={{ fontWeight: 'bold', color: '#7a5d00', marginBottom: 4 }}>
                        Restricciones en {destinationCountryName}
                      </BodyText>
                      <BodyText style={{ color: '#7a5d00' }}>
                        Las siguientes razas requieren licencia especial:{' '}
                        {warnRestrictions.map((r) => r.breedName).join(', ')}.
                      </BodyText>
                    </View>
                  )}
                </View>
              )}

              {/* Número de vuelo y Localizador */}
              <View style={{ flexDirection: 'row', gap: 16 }}>
                <View style={{ flex: 1 }}>
                  <Headline5>Número de Vuelo *</Headline5>
                  <InputContainer>
                    <Input
                      style={{ paddingRight: 0 }}
                      inputProps={{
                        value: formData.flightNumber,
                        onChangeText: (text: string) =>
                          handleInputChange(
                            'flightNumber',
                            text.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 7)
                          ),
                        placeholder: 'Ej: UX7791',
                        maxLength: 7,
                        autoCapitalize: 'characters',
                        style: { backgroundColor: colors.grisClaro }
                      }}
                    />
                    {fieldErrors.flightNumber && <ErrorText>{fieldErrors.flightNumber}</ErrorText>}
                  </InputContainer>
                </View>

                <View style={{ flex: 1 }}>
                  <Headline5>Localizador *</Headline5>
                  <InputContainer>
                    <Input
                      style={{ paddingRight: 0 }}
                      inputProps={{
                        value: formData.locator,
                        onChangeText: (text: string) =>
                          handleInputChange(
                            'locator',
                            text.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6)
                          ),
                        placeholder: 'Ej: PI3VQZ',
                        maxLength: 6,
                        autoCapitalize: 'characters',
                        style: { backgroundColor: colors.grisClaro }
                      }}
                    />
                    {fieldErrors.locator && <ErrorText>{fieldErrors.locator}</ErrorText>}
                  </InputContainer>
                </View>
              </View>

              {/* Fecha de vuelo */}
              <InputWrapper style={{ zIndex: 9999 }}>
                <Headline5>Fecha de Vuelo *</Headline5>
                <InputContainer>
                  <DatePickerWrapper>
                    <DatePicker
                      portalId="root-portal"
                      selected={formData.flightDate}
                      onChange={(date: Date | null) => handleInputChange('flightDate', date)}
                      dateFormat="dd/MM/yyyy"
                      placeholderText="Selecciona la fecha de vuelo"
                      className="datepicker-styled"
                      popperPlacement="bottom-start"
                      minDate={new Date()}
                      locale={getDatePickerLocale()}
                      showMonthDropdown
                      showYearDropdown
                    />
                  </DatePickerWrapper>
                  {fieldErrors.flightDate && <ErrorText>{fieldErrors.flightDate}</ErrorText>}
                </InputContainer>
              </InputWrapper>
            </View>

            {/* ================= SECCIÓN 2: SELECCIÓN DE MASCOTAS ================= */}
            <View style={{ marginBottom: 24 }}>
              <Headline2 style={{ marginBottom: 16, color: colors.azulCorporativo }}>
                ¿Quién viaja contigo?
              </Headline2>

              {/* Carrusel de mascotas */}
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center' }}>
                <ScrollView
                  ref={scrollViewRef}
                  horizontal
                  scrollEventThrottle={16}
                  showsHorizontalScrollIndicator={Platform.OS === 'web'}
                  contentContainerStyle={{ paddingBottom: 16, display: 'flex' }}
                  style={{
                    position: 'relative',
                    zIndex: 1,
                    overflowX: Platform.OS === 'web' ? 'auto' : undefined,
                    display: Platform.OS === 'web' ? 'flex' : undefined
                  } as any}
                >
                  {/* Solo mascotas con PetPass ID activo son seleccionables para viajar. */}
                  {(homeData?.petList ?? []).filter((p: any) => p?.petpassActive === true).map((pet: Pet & { guid: string }) => {
                    const isSelected = formData.selectedPets.includes(pet.guid)
                    return (
                      <TouchableOpacity
                        key={pet.guid}
                        onPress={() => {
                          setSelectedPet(pet.guid)       // foco visual si lo usas
                          togglePetSelection(pet.guid)   // selección múltiple
                        }}
                        style={{ marginRight: 16, alignItems: 'center' }}
                      >
                        <View
                          style={{
                            padding: 3, // crea espacio para el borde blanco
                            borderRadius: 53, // ligeramente mayor que la imagen
                            backgroundColor: colors.blanco, // borde blanco alrededor
                            borderWidth: 2,
                            borderColor: isSelected ? colors.aenaVerde : colors.blanco // borde verde si está seleccionada
                          }}
                        >
                          <Image
                            source={{ uri: pet.avatar }}
                            ref={(ref) => {
                              if (ref) petRefs.current[pet.guid] = ref
                            }}
                            style={{ width: 100, height: 100, borderRadius: 50 }}
                          />
                        </View>
                        <BodyText>{pet.name}</BodyText>
                      </TouchableOpacity>
                    )
                  })}
                </ScrollView>
              </View>
              {(homeData?.petList ?? []).filter((p: any) => p?.petpassActive === true).length === 0 && (
                <BodyText style={{ textAlign: 'center', color: colors.azulCorporativo, marginTop: 8 }}>
                  Ninguna mascota tiene el PetPass ID activo. Completa Documentación nacional, Vacunas y Seguro (aprobado) en el pasaporte digital para poder viajar.
                </BodyText>
              )}
              {fieldErrors.selectedPets && <ErrorText>{fieldErrors.selectedPets}</ErrorText>}

              {/* Listado de mascotas seleccionadas con selector por cada una */}
              {formData.selectedPets.length > 0 && (
                <View style={{ marginTop: 16, gap: 12 }}>
                  {formData.selectedPets.map((guid) => {
                    const pet = getPetByGuid(guid)
                    const options = allowedTypesForPet(pet)
                    const activeIdx = options.findIndex(o => o === formData.petTravelTypes[guid])
                    const weight = getPetWeightKg(pet)
                    const isBrachy = isBrachyPet(pet)
                    const isBrachyBlocked = isBrachy && weight > 10
                    const isBrachyCabinOnly = isBrachy && weight <= 10
                    const destR = getDestinationRestrictionForPet(pet)
                    const isDestBlocked = destR?.restriction === 'block'
                    const isDestWarn = destR?.restriction === 'warn'
                    const hardBlocked = isBrachyBlocked || isDestBlocked
                    const softWarn = !hardBlocked && (isDestWarn || isBrachyCabinOnly)

                    const borderColor = hardBlocked
                      ? colors.rojoInactivo
                      : softWarn
                        ? '#d49a00'
                        : colors.grisMedio
                    const bgColor = hardBlocked
                      ? '#fdecec'
                      : softWarn
                        ? '#fff8e0'
                        : colors.blanco
                    const borderWidth = hardBlocked || softWarn ? 2 : 1

                    const petBreed = getPetBreed(pet)

                    return (
                      <View key={guid} style={{ gap: 4 }}>
                        <View
                          style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            padding: 12,
                            borderWidth,
                            borderColor,
                            borderRadius: 12,
                            backgroundColor: bgColor,
                            gap: 12
                          }}
                        >
                          <Image
                            source={{ uri: pet?.avatar }}
                            style={{ width: 48, height: 48, borderRadius: 24 }}
                          />
                          <View style={{ flex: 1 }}>
                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                              <BodyText style={{ fontWeight: 'bold' }}>{pet?.name}</BodyText>
                              {hardBlocked && (
                                <Text
                                  style={{
                                    fontSize: 14,
                                    color: colors.rojoInactivo,
                                    fontWeight: 'bold',
                                  }}
                                >
                                  ⚠ Bloqueada
                                </Text>
                              )}
                              {!hardBlocked && softWarn && (
                                <Text
                                  style={{
                                    fontSize: 14,
                                    color: '#7a5d00',
                                    fontWeight: 'bold',
                                  }}
                                >
                                  ⚠ Aviso
                                </Text>
                              )}
                            </View>
                            <BodyText style={{ color: colors.grisOscuro }}>
                              Peso: {weight} kg
                              {petBreed ? ` · Raza: ${petBreed}` : ''}
                            </BodyText>
                          </View>

                          <View style={{ minWidth: 160 }}>
                            {options.length === 0 ? (
                              <BodyText style={{ color: colors.rojoInactivo, textAlign: 'right' }}>
                                No puede viajar
                              </BodyText>
                            ) : (
                              <Drop
                                data={options.map(v => ({ id: v, title: v, data: v }))}
                                active={activeIdx}
                                selectedItem={activeIdx}
                                onPress={(item) =>
                                  setFormData(prev => ({
                                    ...prev,
                                    petTravelTypes: { ...prev.petTravelTypes, [guid]: item.data as TravelType }
                                  }))
                                }
                                placeholder="Tipo de viaje"
                                icon={<ChevronDownIcon width={22} height={22} />}
                                activeColor={colors.aenaVerde}
                                color="aena"
                              />
                            )}
                          </View>
                        </View>

                        {/* Mensajes de detalle bajo la card */}
                        {isBrachyBlocked && (
                          <BodyText style={{ color: colors.rojoInactivo, paddingLeft: 12 }}>
                            Raza braquicefálica con peso superior a 10 kg: prohibido viajar por riesgo respiratorio.
                          </BodyText>
                        )}
                        {!isBrachyBlocked && isBrachyCabinOnly && (
                          <BodyText style={{ color: '#7a5d00', paddingLeft: 12 }}>
                            Raza braquicefálica: sólo puede viajar en Cabina (máximo 10 kg con transportín).
                          </BodyText>
                        )}
                        {isDestBlocked && destR && (
                          <BodyText style={{ color: colors.rojoInactivo, paddingLeft: 12 }}>
                            La raza {destR.breedName} tiene la entrada prohibida en {destinationCountryName}.
                            {destR.note ? ` ${destR.note}` : ''}
                          </BodyText>
                        )}
                        {!isDestBlocked && isDestWarn && destR && (
                          <BodyText style={{ color: '#7a5d00', paddingLeft: 12 }}>
                            La raza {destR.breedName} requiere licencia especial en {destinationCountryName}.
                            {destR.note ? ` ${destR.note}` : ''}
                          </BodyText>
                        )}
                      </View>
                    )
                  })}
                  {/* Aviso de peso */}
                  <BodyText style={{ marginTop: 12, fontStyle: 'italic', color: colors.grisOscuro }}>
                    * Los perros y gatos con un peso superior a 8 kg, incluido el transportín, no pueden transportarse en el interior de la cabina.
                  </BodyText>
                  <BodyText style={{ fontStyle: 'italic', color: colors.grisOscuro }}>
                    * Las razas braquicefálicas (hocico chato) sólo pueden viajar en cabina y hasta 10 kg (incluido transportín). Por encima de ese peso no se admite el transporte (Manual GOM A.IV.06 Air Europa).
                  </BodyText>
                </View>
              )}

            </View>

            {/* ================= SECCIÓN 3: DOCUMENTOS DE EXONERACIÓN ================= */}
            <View style={{ marginBottom: 24 }}>
              <Headline2 style={{ marginBottom: 16, color: colors.azulCorporativo }}>
                Documentos de exoneración de responsabilidad
              </Headline2>

              <InputWrapper>
                <Headline5>Nombre *</Headline5>
                <InputContainer>
                  <Input
                    style={{ paddingRight: 0 }}
                    inputProps={{
                      value: formData.ownerName,
                      onChangeText: (text: string) => handleInputChange('ownerName', text),
                      placeholder: 'Introduce tu nombre completo',
                      style: { backgroundColor: colors.grisClaro }
                    }}
                  />
                  {fieldErrors.ownerName && <ErrorText>{fieldErrors.ownerName}</ErrorText>}
                </InputContainer>
              </InputWrapper>

              <InputWrapper>
                <Headline5>DNI / NIE del pasajero *</Headline5>
                <InputContainer>
                  <Input
                    style={{ paddingRight: 0 }}
                    inputProps={{
                      value: formData.passengerDni,
                      onChangeText: (text: string) =>
                        handleInputChange(
                          'passengerDni',
                          text.toUpperCase().replace(/[^0-9A-Z]/g, '').slice(0, 9)
                        ),
                      placeholder: 'Ej: 12345678Z o X1234567L',
                      maxLength: 9,
                      autoCapitalize: 'characters',
                      style: { backgroundColor: colors.grisClaro }
                    }}
                  />
                  {fieldErrors.passengerDni && <ErrorText>{fieldErrors.passengerDni}</ErrorText>}
                </InputContainer>
              </InputWrapper>

              <InputWrapper>
                <Headline5>Nº de teléfono *</Headline5>
                <InputContainer>
                  <Input
                    style={{ paddingRight: 0 }}
                    inputProps={{
                      value: formData.phoneNumber,
                      onChangeText: (text: string) => handleInputChange('phoneNumber', text),
                      placeholder: 'Introduce tu teléfono',
                      keyboardType: 'phone-pad',
                      style: { backgroundColor: colors.grisClaro }
                    }}
                  />
                  {fieldErrors.phoneNumber && <ErrorText>{fieldErrors.phoneNumber}</ErrorText>}
                </InputContainer>
              </InputWrapper>

              <InputWrapper>
                <Headline5>Email de contacto *</Headline5>
                <InputContainer>
                  <Input
                    style={{ paddingRight: 0 }}
                    inputProps={{
                      value: formData.contactEmail,
                      onChangeText: (text: string) => handleInputChange('contactEmail', text),
                      placeholder: 'Introduce tu email',
                      keyboardType: 'email-address',
                      style: { backgroundColor: colors.grisClaro }
                    }}
                  />
                  {fieldErrors.contactEmail && <ErrorText>{fieldErrors.contactEmail}</ErrorText>}
                </InputContainer>
              </InputWrapper>
            </View>

            {/* ================= SECCIÓN 4: CONDICIONES ================= */}
            <View style={{ marginBottom: 24 }}>
              <InputWrapper>
                <View style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
                  <CheckBoxCustom
                    isChecked={formData.acceptsTravelConditions && formData.acceptsTransportConditions}
                    onToggle={(val) =>
                      setFormData(prev => ({
                        ...prev,
                        acceptsTravelConditions: val,
                        acceptsTransportConditions: val
                      }))
                    }
                  />
                  <View style={{ flex: 1 }}>
                    <BodyText>
                      Acepto las condiciones de viaje con mascota y el estado y forma del transportín
                    </BodyText>
                    <Text
                      style={{
                        marginTop: 4,
                        textDecorationLine: 'underline',
                        color: colors.aenaVerde
                      }}
                      onPress={openF244Preview}
                    >
                      Ver descargo de responsabilidad (F244)
                    </Text>
                  </View>
                </View>
                {fieldErrors.acceptsTravelConditions && <ErrorText>{fieldErrors.acceptsTravelConditions}</ErrorText>}
              </InputWrapper>

              <InputWrapper>
                <View style={{ flexDirection: 'row', alignItems: 'flex-start', marginTop: 12 }}>
                  <CheckBoxCustom
                    isChecked={formData.acceptsPrivacyPolicy}
                    onToggle={(val) => handleInputChange('acceptsPrivacyPolicy', val)}
                  />
                  <BodyText style={{ flex: 1 }}>
                    Acepto las{' '}
                    <Text
                      style={{ textDecorationLine: 'underline', color: colors.azulCorporativo }}
                      onPress={() => window.open('https://petpass.pro/politicas-de-privacidad', '_blank')}
                    >
                      políticas de privacidad
                    </Text>
                  </BodyText>
                </View>
                {fieldErrors.acceptsPrivacyPolicy && <ErrorText>{fieldErrors.acceptsPrivacyPolicy}</ErrorText>}
              </InputWrapper>
            </View>

            {/* ================= SECCIÓN 5: FIRMA ================= */}
            <View style={{ marginBottom: 24 }}>
              <Headline2 style={{ marginBottom: 16, color: colors.azulCorporativo }}>
                Firma Digital
              </Headline2>

              <InputWrapper>
                <View
                  style={{
                    borderWidth: 1,
                    borderColor: colors.grisMedio,
                    borderRadius: 8,
                    backgroundColor: colors.blanco,
                    padding: 16,
                    alignItems: 'center'
                  }}
                >
                  <canvas
                    ref={signatureCanvasRef}
                    width={400}
                    height={200}
                    style={{
                      border: `1px dashed ${colors.grisMedio}`,
                      borderRadius: 4,
                      cursor: 'crosshair',
                      width: '100%',
                      maxWidth: '400px',
                      height: '200px',
                      touchAction: 'none',
                      display: 'block'
                    }}
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                    onTouchCancel={stopDrawing}
                  />

                  <View style={{ flexDirection: 'row', marginTop: 12, gap: 12 }}>
                    <TouchableOpacity
                      onPress={clearSignature}
                      style={{
                        paddingVertical: 8,
                        paddingHorizontal: 16,
                        borderRadius: 20,
                        borderWidth: 1,
                        borderColor: colors.rojoInactivo,
                        backgroundColor: 'transparent'
                      }}
                    >
                      <BodyText style={{ color: colors.rojoInactivo }}>Borrar</BodyText>
                    </TouchableOpacity>
                  </View>
                </View>
                {fieldErrors.signature && <ErrorText>{fieldErrors.signature}</ErrorText>}
              </InputWrapper>
            </View>
          </ScrollView>
        </BottomActionContainer>
      </View>
    </AppBackground>
  )
}

// ===== Checkbox inline component =====
const CheckBoxCustom: React.FC<{ isChecked: boolean; onToggle: (checked: boolean) => void }> = ({
  isChecked,
  onToggle
}) => (
  <TouchableOpacity
    onPress={() => onToggle(!isChecked)}
    style={{
      width: 20,
      height: 20,
      borderRadius: 4,
      borderWidth: 2,
      borderColor: colors.grisMedio,
      backgroundColor: isChecked ? colors.aenaVerde : 'transparent',
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: 8
    }}
  >
    {isChecked && (
      <View
        style={{
          width: 10,
          height: 10,
          backgroundColor: colors.aenaVerde,
          borderRadius: 2
        }}
      />
    )}
  </TouchableOpacity>
)

export default AenaTravelAdd
